import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

export const SUPPRESSION_PAGE_SIZE = 50;

export const SUPPRESSION_REASONS = [
  'opt_out',
  'do_not_contact',
  'carrier_block',
  'manual',
  'vendor_dnc',
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

export const REASON_LABELS: Record<SuppressionReason, string> = {
  opt_out: 'Opted out',
  do_not_contact: 'Do not contact',
  carrier_block: 'Carrier block',
  manual: 'Do not contact (manual)',
  vendor_dnc: 'Do not contact (lead vendor)',
};

export interface SuppressionFilters {
  status: 'active' | 'released';
  reason: SuppressionReason | 'any';
  /** Digits of a phone number. */
  phone: string;
}

export interface SuppressionEntry {
  id: string;
  phone: string;
  reason: SuppressionReason;
  source: string;
  note: string | null;
  merchantId: string | null;
  merchantName: string | null;
  createdBy: string | null;
  createdAt: string;
  active: boolean;
  releasedAt: string | null;
  releasedBy: string | null;
  releaseNote: string | null;
}

/** Only the merchant (START) lifts these; the database refuses a person's release. */
export function liftedOnlyByMerchant(reason: SuppressionReason): boolean {
  return reason === 'opt_out' || reason === 'carrier_block';
}

/** Where an entry came from, in words. */
export function describeSource(source: string): string {
  if (source.startsWith('keyword:'))
    return `Texted "${source.slice('keyword:'.length).toUpperCase()}"`;
  if (source.startsWith('import:')) return 'Lead file (vendor flag)';
  if (source.startsWith('intake:')) return 'Webhook lead (vendor flag)';
  switch (source) {
    case 'telnyx_autoresponse':
      return "Carrier's STOP handling";
    case 'telnyx':
      return 'Refused by the carrier (40300)';
    case 'keyword_review':
      return 'Possible opt-out text (review)';
    case 'user':
      return 'Added by a person';
    case 'pre_m6_stop_lock':
      return 'Early safety lock';
    case 'migrated':
      return 'Moved from an older record';
    default:
      return source;
  }
}

type Row = {
  id: string;
  phone: string;
  reason: SuppressionReason;
  source: string;
  note: string | null;
  merchant_id: string | null;
  created_by: string | null;
  created_at: string;
  active: boolean;
  released_at: string | null;
  released_by: string | null;
  release_note: string | null;
};

const COLUMNS =
  'id, phone, reason, source, note, merchant_id, created_by, created_at, active, released_at, released_by, release_note';

async function withMerchantNames(workspaceId: string, rows: Row[]): Promise<SuppressionEntry[]> {
  const ids = [...new Set(rows.flatMap((r) => (r.merchant_id ? [r.merchant_id] : [])))];
  const names = new Map<string, string | null>();
  if (ids.length > 0) {
    const { data, error } = await supabase()
      .from('merchants')
      .select('id, business_name, owner_name')
      .eq('workspace_id', workspaceId)
      .in('id', ids);
    if (error) throw new Error(`Couldn't load merchant names: ${error.message}`);
    for (const m of data) names.set(m.id, m.business_name ?? m.owner_name);
  }
  return rows.map((r) => ({
    id: r.id,
    phone: r.phone,
    reason: r.reason,
    source: r.source,
    note: r.note,
    merchantId: r.merchant_id,
    merchantName: r.merchant_id ? (names.get(r.merchant_id) ?? null) : null,
    createdBy: r.created_by,
    createdAt: r.created_at,
    active: r.active,
    releasedAt: r.released_at,
    releasedBy: r.released_by,
    releaseNote: r.release_note,
  }));
}

/** One page of the suppression list (admins; RLS returns nothing to reps). */
export async function fetchSuppressionPage(
  workspaceId: string,
  filters: SuppressionFilters,
  offset: number,
): Promise<SuppressionEntry[]> {
  let query = supabase()
    .from('suppression_list')
    .select(COLUMNS)
    .eq('workspace_id', workspaceId)
    .eq('active', filters.status === 'active');
  if (filters.reason !== 'any') query = query.eq('reason', filters.reason);
  const digits = filters.phone.replace(/\D/g, '');
  if (digits) query = query.like('phone', `%${digits}%`);
  const { data, error } = await query
    .order(filters.status === 'active' ? 'created_at' : 'released_at', { ascending: false })
    .order('id')
    .range(offset, offset + SUPPRESSION_PAGE_SIZE - 1);
  if (error) throw new Error(`Couldn't load the suppression list: ${error.message}`);
  return withMerchantNames(workspaceId, data);
}

export function useSuppressionQuery(workspaceId: string, filters: SuppressionFilters) {
  return useInfiniteQuery({
    queryKey: queryKeys.suppressionList(workspaceId, filters),
    queryFn: ({ pageParam }) => fetchSuppressionPage(workspaceId, filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === SUPPRESSION_PAGE_SIZE ? pages.length * SUPPRESSION_PAGE_SIZE : undefined,
  });
}

/** Active entries for one merchant's phone (admins). */
export function useMerchantSuppressionQuery(
  workspaceId: string,
  merchantId: string,
  phone: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.merchantSuppression(workspaceId, merchantId),
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('suppression_list')
        .select(COLUMNS)
        .eq('workspace_id', workspaceId)
        .eq('phone', phone)
        .eq('active', true)
        .order('created_at');
      if (error) throw new Error(`Couldn't load the suppression entries: ${error.message}`);
      return withMerchantNames(workspaceId, data);
    },
  });
}

export function useSuppressionMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.suppression(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.merchants(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) }),
    ]);
  };

  /** Adds a manual do-not-contact. Resolves false when one is already active. */
  const add = useMutation({
    mutationFn: async (input: { phone: string; note: string }) => {
      const { data, error } = await supabase().rpc('add_suppression', {
        p_phone: input.phone,
        p_note: input.note.trim() === '' ? undefined : input.note.trim(),
      });
      if (error) {
        throw new Error(
          error.message.includes('row-level security')
            ? 'You can only mark merchants assigned to you.'
            : error.message.charAt(0).toUpperCase() + error.message.slice(1),
        );
      }
      return data;
    },
    onSettled: refresh,
  });

  const release = useMutation({
    mutationFn: async (input: { id: string; note: string }) => {
      const { data, error } = await supabase().rpc('release_suppression', {
        p_id: input.id,
        p_note: input.note,
      });
      if (error) throw new Error(error.message.charAt(0).toUpperCase() + error.message.slice(1));
      if (!data) throw new Error('This entry was already released.');
    },
    onSettled: refresh,
  });

  return { add, release };
}
